import type { KnowledgeBaseClient } from './knowledge-base-client';
import type { RAGDocument, BusinessInfo } from '../../types';
import type { ZillisConfig } from '../../config/config-types';
import { logger } from '../logger';

/**
 * Zillis Cloud implementation of KnowledgeBaseClient
 */
export class ZillisKnowledgeBaseClient implements KnowledgeBaseClient {
  private config: ZillisConfig;

  constructor(config: ZillisConfig) {
    this.config = config;
  }

  /**
   * Retrieve relevant documents from Zillis Cloud knowledge base
   */
  async retrieveContext(
    query: string,
    userId: string
  ): Promise<{
    knowledgeBaseDocs: RAGDocument[];
    userBusinessContext: BusinessInfo | null;
    contextText: string;
  }> {
    try {
      // Query Zillis Cloud knowledge base
      const kbResponse = await fetch(
        `${this.config.baseUrl}/api/v1/knowledge-bases/${this.config.knowledgeBaseId}/search`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.config.apiKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query,
            limit: 10,
            threshold: 0.5,
          }),
        }
      );

      if (!kbResponse.ok) {
        logger.error('Zillis KB search failed', undefined, { op: 'ZillisKnowledgeBaseClient.retrieveContext', statusText: kbResponse.statusText });
        return {
          knowledgeBaseDocs: [],
          userBusinessContext: null,
          contextText: '',
        };
      }

      const kbData = await kbResponse.json();
      const knowledgeBaseDocs: RAGDocument[] = (kbData.results || []).map(
        (doc: any) => ({
          id: doc.id,
          content: doc.content,
          source: doc.source || 'zillis-kb',
          score: doc.score,
          metadata: doc.metadata,
        })
      );

      // Retrieve user's business context
      let userBusinessContext: BusinessInfo | null = null;
      try {
        // Note: Business context comes from Supabase user profile
        // It's passed in the request body, not retrieved from Zillis Cloud
        logger.debug('business context is retrieved from Supabase user profile', { op: 'ZillisKnowledgeBaseClient.retrieveContext' });
      } catch (error) {
        logger.error('failed to retrieve user context from Zillis', error, { op: 'ZillisKnowledgeBaseClient.retrieveContext' });
      }

      // Build context text
      const contextText = knowledgeBaseDocs
        .map((doc) => `Source: ${doc.source}\n${doc.content}`)
        .join('\n\n---\n\n');

      return {
        knowledgeBaseDocs,
        userBusinessContext,
        contextText,
      };
    } catch (error) {
      logger.error('Zillis retrieveContext error', error, { op: 'ZillisKnowledgeBaseClient.retrieveContext' });
      return {
        knowledgeBaseDocs: [],
        userBusinessContext: null,
        contextText: '',
      };
    }
  }

  /**
   * Store user's business context in Supabase user profile
   * 
   * Note: Business context is stored as user metadata in Supabase,
   * not in Zillis Cloud. This method is a no-op for the Zillis client.
   */
  async storeUserContext(userId: string, context: BusinessInfo): Promise<void> {
    try {
      logger.debug('storeUserContext called', {
        op: 'ZillisKnowledgeBaseClient.storeUserContext',
        userId,
        contextKeys: Object.keys(context),
        note: 'Business context is stored in AWS Cognito user profile',
      });
      // Business context storage is handled by AWS Cognito admin APIs
      // This is managed outside of the knowledge base client
    } catch (error) {
      logger.error('Zillis storeUserContext error', error, { op: 'ZillisKnowledgeBaseClient.storeUserContext' });
    }
  }

  /**
   * Retrieve user's stored business context from Supabase user profile
   * 
   * Note: Business context is stored as user metadata in Supabase,
   * not in Zillis Cloud. This method returns null as retrieval is handled
   * by passing the user profile in the request.
   */
  async getUserContext(userId: string): Promise<BusinessInfo | null> {
    try {
      logger.debug('getUserContext called', {
        op: 'ZillisKnowledgeBaseClient.getUserContext',
        userId,
        note: 'Business context is stored in Supabase user profile',
      });
      // Business context is passed in the request body from the client
      // It comes from Supabase user metadata
      return null;
    } catch (error) {
      logger.error('Zillis getUserContext error', error, { op: 'ZillisKnowledgeBaseClient.getUserContext' });
      return null;
    }
  }
}



